import { isCommunicationsLayerBlock, isLegacyScheduleBlock } from './communicationBlocks';
import { cardCurrentMessage, cardCurrentTitle, cardKey } from './communicationCardState';

import type {
    CommunicationBlockDefinition,
    CommunicationBlockIdValue,
} from './communicationBlocks';
import type { CommunicationCardKey, CommunicationCardState } from './communicationCardState';
import type { TranslationKey } from '@/i18n-key';

import { CommunicationEmailAudience } from '@/pages/CycleEvaluation/Communications/communicationEmailAudience';
import { emailMessageSchema, emailTitleSchema } from '@/pages/shared/ScheduleCreation/validation';
import { validationMessageKey } from '@/utils/validationMessage';

export const getEmailCardValidationError = (
    card: CommunicationCardState | undefined,
): TranslationKey | undefined => {
    if (!card || !card.enabled) {
        return undefined;
    }

    const titleResult = emailTitleSchema.safeParse(cardCurrentTitle(card));
    if (!titleResult.success) {
        return validationMessageKey(titleResult.error.issues[0]?.message);
    }

    const messageResult = emailMessageSchema.safeParse(cardCurrentMessage(card));
    if (!messageResult.success) {
        return validationMessageKey(messageResult.error.issues[0]?.message);
    }

    return undefined;
};

export interface GetFirstInvalidCommunicationErrorParams {
    visibleBlocks: CommunicationBlockDefinition[];
    blockEnabled: Partial<Record<CommunicationBlockIdValue, boolean>>;
    cards: Partial<Record<CommunicationCardKey, CommunicationCardState>>;
}

export const getFirstInvalidCommunicationError = ({
    visibleBlocks,
    blockEnabled,
    cards,
}: GetFirstInvalidCommunicationErrorParams): TranslationKey | undefined => {
    for (const block of visibleBlocks) {
        if (!(blockEnabled[block.id] ?? true)) {
            continue;
        }

        if (!isLegacyScheduleBlock(block) && !isCommunicationsLayerBlock(block)) {
            continue;
        }

        const managerError = getEmailCardValidationError(
            cards[cardKey(block.id, CommunicationEmailAudience.MANAGER)],
        );
        if (managerError) {
            return managerError;
        }

        if (block.hasEmployeeCard) {
            const employeeError = getEmailCardValidationError(
                cards[cardKey(block.id, CommunicationEmailAudience.EMPLOYEE)],
            );
            if (employeeError) {
                return employeeError;
            }
        }
    }

    return undefined;
};
